import type {
  Ack,
  ClientToServerEvents,
  ErrorCode,
  JoinedPayload,
  Key,
  KeyAction,
  RoomSettings,
  ServerToClientEvents,
} from '@sprinters/shared';
import { io, type Socket } from 'socket.io-client';
import { SnapshotBuffer } from '../game/SnapshotBuffer';
import { store, type Store } from '../state/store';
import { loadSession, saveSession } from './session';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const PING_INTERVAL_MS = 3000;

export type ActionResult = { ok: true } | { ok: false; error: ErrorCode };

function toResult(ack: Ack<unknown>): ActionResult {
  return ack.ok ? { ok: true } : { ok: false, error: ack.error };
}

/** Passerelle entre le serveur (Socket.IO) et l'état de l'application. */
export class GameClient {
  /** Snapshots de la série suivie, lus à chaque image par le moteur de rendu. */
  readonly feed = new SnapshotBuffer();

  private readonly socket: GameSocket;
  private pingTimer: number | null = null;
  /** Appelé quand l'hôte nous exclut du salon. */
  onKicked: (() => void) | null = null;

  constructor(private readonly state: Store = store) {
    this.socket = io({ autoConnect: false });
    this.registerListeners();
    this.socket.connect();
  }

  private registerListeners(): void {
    const { socket, state } = this;

    socket.on('connect', () => {
      state.set({ connection: 'connected' });
      this.startPinging();
      void this.resumeStoredSession();
    });

    socket.on('disconnect', () => {
      state.set({ connection: 'disconnected' });
      this.stopPinging();
    });

    socket.on('room:state', (room) => state.set({ room }));

    socket.on('room:kicked', () => {
      this.resetLocalState();
      this.onKicked?.();
    });

    socket.on('tournament:state', (tournament) => {
      state.set({ tournament, tournamentReceivedAt: performance.now() });
      if (!tournament) this.feed.clear();
    });

    socket.on('tournament:step', (payload) => {
      const { tournament } = state.get();
      if (!tournament) return;
      state.set({
        tournament: {
          ...tournament,
          roundIndex: payload.roundIndex,
          step: payload.step,
          stepRemainingMs: payload.stepRemainingMs,
        },
        tournamentReceivedAt: performance.now(),
      });
    });

    socket.on('tournament:heat', (heat) => {
      const { tournament } = state.get();
      if (!tournament) return;
      const rounds = tournament.rounds.map((round) =>
        round.index === heat.roundIndex
          ? { ...round, heats: round.heats.map((h) => (h.id === heat.id ? heat : h)) }
          : round,
      );
      state.set({ tournament: { ...tournament, rounds } });
    });

    socket.on('race:start', ({ heatId, startsInMs }) => {
      if (state.get().watchingHeatId !== heatId) return;
      state.set({ goAt: performance.now() + startsInMs });
    });

    socket.on('race:watching', ({ heatId }) => {
      if (heatId !== state.get().watchingHeatId) this.feed.clear();
      state.set({ watchingHeatId: heatId });
    });

    socket.on('race:snapshot', (snapshot) => {
      this.feed.push(snapshot, performance.now());
    });
  }

  // --- Actions ---------------------------------------------------------------------------

  async createRoom(nickname: string, settings?: Partial<RoomSettings>): Promise<ActionResult> {
    const ack = await this.request<JoinedPayload>((cb) =>
      this.socket.emit('room:create', { nickname, ...(settings && { settings }) }, cb),
    );
    return this.handleJoined(ack);
  }

  async joinRoom(code: string, nickname: string): Promise<ActionResult> {
    const ack = await this.request<JoinedPayload>((cb) =>
      this.socket.emit('room:join', { code, nickname }, cb),
    );
    return this.handleJoined(ack);
  }

  leaveRoom(): void {
    this.socket.emit('room:leave');
    this.resetLocalState();
  }

  async configure(settings: Partial<RoomSettings>): Promise<ActionResult> {
    return toResult(await this.request((cb) => this.socket.emit('room:configure', settings, cb)));
  }

  async kick(playerId: string): Promise<ActionResult> {
    return toResult(await this.request((cb) => this.socket.emit('room:kick', { playerId }, cb)));
  }

  async startTournament(): Promise<ActionResult> {
    return toResult(await this.request((cb) => this.socket.emit('tournament:start', cb)));
  }

  async resetTournament(): Promise<ActionResult> {
    return toResult(await this.request((cb) => this.socket.emit('tournament:reset', cb)));
  }

  watchHeat(heatId: string): void {
    this.socket.emit('race:watch', { heatId });
  }

  sendInput(key: Key, action: KeyAction, t: number): void {
    this.socket.emit('race:input', { key, action, t });
  }

  // --- Internes --------------------------------------------------------------------------

  private request<T = undefined>(emit: (ack: (res: Ack<T>) => void) => void): Promise<Ack<T>> {
    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => resolve({ ok: false, error: 'INTERNAL' }), 8000);
      emit((res) => {
        window.clearTimeout(timeout);
        resolve(res);
      });
    });
  }

  private handleJoined(ack: Ack<JoinedPayload>): ActionResult {
    if (!ack.ok) return { ok: false, error: ack.error };
    const { code, playerId, sessionToken } = ack.data;
    saveSession({ code, sessionToken });
    this.state.set({ session: { code, playerId } });
    return { ok: true };
  }

  private async resumeStoredSession(): Promise<void> {
    const stored = loadSession();
    if (!stored) return;
    const ack = await this.request<JoinedPayload>((cb) =>
      this.socket.emit('room:resume', stored, cb),
    );
    if (ack.ok) {
      this.handleJoined(ack);
    } else if (ack.error !== 'INTERNAL') {
      // Session ou salon disparus : retour à l'accueil.
      this.resetLocalState();
    }
  }

  private resetLocalState(): void {
    saveSession(null);
    this.feed.clear();
    this.state.set({
      session: null,
      room: null,
      tournament: null,
      watchingHeatId: null,
      goAt: null,
    });
  }

  private startPinging(): void {
    this.stopPinging();
    const ping = (): void => {
      const sentAt = performance.now();
      this.socket.emit('time:ping', () => {
        this.state.set({ pingMs: Math.round(performance.now() - sentAt) });
      });
    };
    ping();
    this.pingTimer = window.setInterval(ping, PING_INTERVAL_MS);
  }

  private stopPinging(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.state.set({ pingMs: null });
  }
}
